import { useEffect, useState } from "react";
import { ChatCircleText, PaperPlaneTilt, ArrowsClockwise, CheckCircle, XCircle, Warning } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SmsRoutingMatrix from "@/pages/SmsRoutingMatrix";
import { formatLocal, formatLocalDate } from "@/lib/timezone";

const STATUS_COLORS = {
  delivered:    "bg-[#F0FDF4] text-[#16A34A] border-[#BBF7D0]",
  sent:         "bg-[#EEF2FF] text-[#4338CA] border-[#C7D2FE]",
  queued:       "bg-[#F5F5F5] text-[#525252] border-[#E5E5E5]",
  sending:      "bg-[#F5F5F5] text-[#525252] border-[#E5E5E5]",
  accepted:     "bg-[#F5F5F5] text-[#525252] border-[#E5E5E5]",
  undelivered:  "bg-[#FFFBEB] text-[#D97706] border-[#FDE68A]",
  failed:       "bg-[#FEF2F2] text-[#DC2626] border-[#FECACA]",
};

export default function AdminSmsTab() {
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ to: "", body: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [cfg, st, rec] = await Promise.all([
        api.get("/admin/sms/config"),
        api.get("/admin/sms/stats").catch(() => ({ data: null })),
        api.get("/admin/sms/recent?limit=100"),
      ]);
      setConfig(cfg.data);
      setStats(st.data);
      setMessages(rec.data.messages || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load SMS console");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const sendTest = async () => {
    const to = (form.to || "").trim();
    if (!to.startsWith("+") || to.length < 8) {
      toast.error("Enter an E.164 number, e.g. +14155550123");
      return;
    }
    setSending(true);
    try {
      await api.post("/admin/sms/test-send", { to, body: form.body || null });
      toast.success(`Test SMS queued for ${to}`);
      setForm({ ...form, body: "" });
      setTimeout(load, 2500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
    setSending(false);
  };

  if (loading) return <div className="p-8 flex justify-center"><div className="w-8 h-8 border-2 border-[#0055FF] border-t-transparent animate-spin" /></div>;

  const notConfigured = !config?.configured;
  const totalErrors = messages.filter(m => m.error_code).length;

  return (
    <div className="space-y-5" data-testid="admin-sms-tab">
      {/* Config / status strip */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className={`border p-4 ${notConfigured ? "border-[#FECACA] bg-[#FEF2F2]" : "border-[#BBF7D0] bg-[#F0FDF4]"}`}>
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#525252]">Twilio Status</div>
          <div className={`text-lg font-black mt-1 flex items-center gap-2 ${notConfigured ? "text-[#DC2626]" : "text-[#16A34A]"}`}>
            {notConfigured ? <><XCircle size={18} /> Not configured</> : <><CheckCircle size={18} /> Live</>}
          </div>
          <div className="text-[11px] text-[#525252] mt-0.5">
            {notConfigured ? "Missing TWILIO_ACCOUNT_SID / TWILIO_AUTH_TOKEN" : `Account ${config.account_sid_prefix}`}
          </div>
        </div>
        <div className="border border-[#E5E5E5] bg-white p-4">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Sent Today</div>
          <div className="text-2xl font-black text-[#0A0A0A] mt-1 tracking-tight" data-testid="sms-stat-today">
            {stats?.today ?? "—"}
          </div>
          <div className="text-[11px] text-[#525252] mt-0.5">from Twilio message log</div>
        </div>
        <div className="border border-[#E5E5E5] bg-white p-4">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Sent This Month</div>
          <div className="text-2xl font-black text-[#0A0A0A] mt-1 tracking-tight" data-testid="sms-stat-month">
            {stats?.month ?? "—"}
          </div>
          <div className="text-[11px] text-[#525252] mt-0.5">calendar month</div>
        </div>
        <div className="border border-[#E5E5E5] bg-white p-4" data-testid="sms-stat-cost">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Cost (Month)</div>
          <div className="text-2xl font-black text-[#0A0A0A] mt-1 tracking-tight">
            {stats?.month_cost != null ? `$${Number(stats.month_cost).toFixed(2)}` : "—"}
          </div>
          <div className="text-[11px] text-[#525252] mt-0.5">
            {stats?.today_cost != null ? `today $${Number(stats.today_cost).toFixed(2)}` : "live from Twilio"}
          </div>
        </div>
        <div className="border border-[#E5E5E5] bg-white p-4">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Errors (visible)</div>
          <div className={`text-2xl font-black mt-1 tracking-tight ${totalErrors > 0 ? "text-[#DC2626]" : "text-[#0A0A0A]"}`} data-testid="sms-stat-errors">
            {totalErrors}
          </div>
          <div className="text-[11px] text-[#525252] mt-0.5">in last {messages.length} messages</div>
        </div>
      </div>

      {/* Wiring summary */}
      <div className="border border-[#E5E5E5] bg-white p-4 text-xs">
        <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3] mb-2">Current Wiring</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 font-mono">
          <div><span className="text-[#A3A3A3]">From number:</span> <strong>{config?.from_number || "—"}</strong></div>
          <div><span className="text-[#A3A3A3]">Messaging Service:</span> <strong>{config?.messaging_service_sid || "—"}</strong></div>
          <div className="md:col-span-2"><span className="text-[#A3A3A3]">Audience routing:</span> <strong>role-based (see matrix below)</strong></div>
        </div>
      </div>

      {/* Routing matrix */}
      <SmsRoutingMatrix />

      {/* Test send */}
      <div className="border border-[#E5E5E5] bg-white">
        <div className="bg-[#FAFAFA] px-4 py-3 border-b border-[#E5E5E5]">
          <h3 className="text-sm font-bold text-[#0A0A0A]">Send Test SMS</h3>
          <p className="text-[10px] text-[#A3A3A3] mt-0.5">Fire an ad-hoc message to any E.164 number to verify delivery end-to-end.</p>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="md:col-span-2">
            <Label className="text-[10px] font-bold uppercase text-[#525252]">To (E.164)</Label>
            <Input value={form.to} onChange={e => setForm({ ...form, to: e.target.value })} placeholder="+14155550123" className="rounded-none mt-1 text-xs font-mono" data-testid="sms-test-to" />
          </div>
          <div className="md:col-span-3">
            <Label className="text-[10px] font-bold uppercase text-[#525252]">Body <span className="text-[#A3A3A3] normal-case font-normal">(optional — a default test message is used if blank)</span></Label>
            <Input value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} placeholder="Leave blank for default" className="rounded-none mt-1 text-xs" data-testid="sms-test-body" />
          </div>
          <div className="md:col-span-5 flex justify-end gap-2">
            <Button onClick={load} variant="outline" className="rounded-none text-xs"><ArrowsClockwise size={13} className="mr-1.5" /> Refresh log</Button>
            <Button onClick={sendTest} disabled={sending || notConfigured} className="rounded-none bg-[#0055FF] hover:bg-[#003ECC] text-white text-xs" data-testid="sms-test-send-btn">
              <PaperPlaneTilt size={13} className="mr-1.5" /> {sending ? "Sending…" : "Send Test"}
            </Button>
          </div>
        </div>
      </div>

      {/* Message log */}
      <div className="border border-[#E5E5E5] bg-white">
        <div className="bg-[#FAFAFA] px-4 py-3 border-b border-[#E5E5E5] flex items-center gap-2">
          <ChatCircleText size={14} />
          <h3 className="text-sm font-bold text-[#0A0A0A]">Message Log</h3>
          <p className="text-[10px] text-[#A3A3A3] ml-auto">Live from Twilio — last {messages.length} messages (both directions).</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5] text-[10px] uppercase tracking-wider text-[#525252]">
                <th className="px-3 py-2 text-left font-bold">Sent</th>
                <th className="px-3 py-2 text-left font-bold">Dir</th>
                <th className="px-3 py-2 text-left font-bold">To</th>
                <th className="px-3 py-2 text-left font-bold">Status</th>
                <th className="px-3 py-2 text-left font-bold">Body</th>
                <th className="px-3 py-2 text-right font-bold">Price</th>
                <th className="px-3 py-2 text-left font-bold">Error</th>
              </tr>
            </thead>
            <tbody>
              {messages.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-10 text-center text-[#A3A3A3]">No messages yet. Send a test above to confirm delivery.</td></tr>
              )}
              {messages.map(m => {
                const pill = STATUS_COLORS[m.status] || "bg-[#F5F5F5] text-[#525252] border-[#E5E5E5]";
                const dateStr = m.date_sent ? formatLocal(m.date_sent) : "—";
                return (
                  <tr key={m.sid} className="border-b border-[#F5F5F5] hover:bg-[#FAFAFA]" data-testid={`sms-msg-${m.sid}`}>
                    <td className="px-3 py-2 whitespace-nowrap font-mono text-[10px] text-[#525252]">{dateStr}</td>
                    <td className="px-3 py-2"><span className="text-[10px] uppercase font-bold text-[#A3A3A3]">{m.direction?.replace("outbound-", "") || "-"}</span></td>
                    <td className="px-3 py-2 font-mono text-[11px]">{m.to}</td>
                    <td className="px-3 py-2"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 border ${pill}`}>{m.status}</span></td>
                    <td className="px-3 py-2 text-[#0A0A0A] max-w-[420px] truncate" title={m.body}>{m.body}</td>
                    <td className="px-3 py-2 text-right font-mono text-[11px] text-[#525252]">{m.price ? `${m.price} ${m.price_unit || ""}`.trim() : "—"}</td>
                    <td className="px-3 py-2">
                      {m.error_code ? (
                        <span className="inline-flex items-center gap-1 text-[#DC2626] font-bold text-[11px]" title={m.error_message || ""}>
                          <Warning size={12} weight="fill" /> {m.error_code}
                        </span>
                      ) : <span className="text-[#A3A3A3]">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
